/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   BlastRadiusGraph.tsx
 *     Feature         :   117-operational-dashboards
 *     Description     :   React Flow network graph for capacity dependencies
 *     Constitution    :   v1.12.0 | Enterprise Dependency Architecture
 *     Author          :   Peter Dobler
 *     Created         :   2026-02-05
 * =========================================================================================
 */

import React, { useMemo } from 'react';
import ReactFlow, { Background, Controls, MiniMap, Node, Edge, Position } from 'reactflow';
import 'reactflow/dist/style.css';
import type { DependencyNode, DependencyEdge } from '../../services/operations/slaService';

// Node kinds in the real dependency network (capacity_type carries the kind)
const TYPE_COLORS: Record<string, string> = {
  Lakehouse: '#3b82f6',
  Warehouse: '#8b5cf6',
  Workspace: '#f59e0b',
  Capacity: '#10b981',
};

const STATUS_BORDER: Record<string, string> = {
  Healthy: '#22c55e',
  Warning: '#eab308',
  Critical: '#ef4444',
};

interface BlastRadiusGraphProps {
  nodes: DependencyNode[];
  edges: DependencyEdge[];
  impactedNodes?: string[];
  selectedNode?: string;
  onNodeClick?: (nodeId: string) => void;
}

export const BlastRadiusGraph: React.FC<BlastRadiusGraphProps> = ({
  nodes: rawNodes,
  edges: rawEdges,
  impactedNodes = [],
  selectedNode,
  onNodeClick,
}) => {
  const impactedSet = useMemo(() => new Set(impactedNodes), [impactedNodes]);

  const flowNodes: Node[] = useMemo(() => {
    // Grid layout: one column per capacity type, rows within each column.
    // Uses current canonical type names (Spec 179 — Fabric/PowerBI/MsManaged/Unknown).
    // Columns: source assets (lakehouse/warehouse) -> workspaces -> capacities
    const typeIndex: Record<string, number> = { Lakehouse: 0, Warehouse: 0, Workspace: 1, Capacity: 2 };
    const typeCounters: Record<string, number> = {};

    return rawNodes.map((n) => {
      const typeCol = typeIndex[n.capacity_type] ?? 1;
      typeCounters[n.capacity_type] = (typeCounters[n.capacity_type] ?? 0) + 1;
      const row = typeCounters[n.capacity_type];
      const isImpacted = impactedSet.has(n.id);
      const isSelected = n.id === selectedNode;

      return {
        id: n.id,
        position: { x: typeCol * 280 + 50, y: row * 120 + 50 },
        data: {
          label: (
            <div className="text-xs text-center">
              <div className="font-bold">{n.capacity_name}</div>
              <div className="text-[var(--color-text-secondary)]">{n.capacity_type}</div>
              {n.workspace_count > 0 && (
                <div className="text-[var(--color-text-secondary)]">{n.workspace_count} consumers</div>
              )}
            </div>
          ),
        },
        sourcePosition: Position.Right,
        targetPosition: Position.Left,
        style: {
          backgroundColor: isImpacted ? '#fef2f2' : '#ffffff',
          border: `2px solid ${isSelected ? '#6366f1' : STATUS_BORDER[n.status] ?? '#d1d5db'}`,
          borderRadius: '0.5rem',
          padding: '8px',
          minWidth: '160px',
          boxShadow: isImpacted ? '0 0 8px rgba(239, 68, 68, 0.4)' : undefined,
        },
      };
    });
  }, [rawNodes, impactedSet, selectedNode]);

  const flowEdges: Edge[] = useMemo(() => {
    return rawEdges.map((e, i) => ({
      id: `edge-${i}`,
      source: e.source,
      target: e.target,
      animated: impactedSet.has(e.target),
      style: {
        stroke: impactedSet.has(e.target) ? '#ef4444' : '#9ca3af',
        strokeWidth: impactedSet.has(e.target) ? 2 : 1,
      },
      label: e.dependency_type,
      labelStyle: { fontSize: 10, fill: '#6b7280' },
    }));
  }, [rawEdges, impactedSet]);

  if (rawNodes.length === 0) {
    return (
      <div className="flex items-center justify-center h-96 text-[var(--color-text-secondary)]">
        No dependency data available
      </div>
    );
  }

  return (
    <div className="h-[500px] w-full border border-[var(--color-border)] rounded-lg overflow-hidden">
      <ReactFlow
        nodes={flowNodes}
        edges={flowEdges}
        onNodeClick={(_e, node) => onNodeClick?.(node.id)}
        fitView
        attributionPosition="bottom-left"
      >
        <Background />
        <Controls />
        <MiniMap
          nodeColor={(n) => {
            const raw = rawNodes.find(r => r.id === n.id);
            return raw ? (TYPE_COLORS[raw.capacity_type] ?? '#d1d5db') : '#d1d5db';
          }}
          maskColor="rgba(0,0,0,0.1)"
        />
      </ReactFlow>
    </div>
  );
};
